import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { ModalComponent } from "../..";
import { useCardTokenization } from "../../../../customHooks/useCardTokenization";
import { useEnvironment } from "../../../../customHooks/useEnvironment";
import { useModalControl } from "../../../../customHooks/useModalControl";
import { SingleCooperativeDetails } from "../../../../redux/features/cooperative/cooperative.type";
import {
  useGetMemberBankAccountsQuery,
  useGetUserDebitCardsQuery,
  useSetUpDirectDebitMutation,
} from "../../../../redux/features/payment/payment.api";
import {
  SingleDebitCard,
  SingleMemberBankAccount,
} from "../../../../redux/features/payment/payment.type";
import { Button } from "../../../buttons/button";
import { BankLoader } from "../../../cards/bankCard";
import { DebitCardLoader } from "../../../cards/debitcard";
import { Icon } from "../../../icons/icon";
import { Checkbox } from "../../../inputs/checkbox";
import { AddBankAccountModal } from "../AddBankAccountModal";

export type InstrumentType = "card" | "bank";

export const ChooseDebitInstrumentModal = ({
  isModalOpen,
  cooperativeDetails,
  type = ["bank", "card"],
  mode = "linkToCoop",
  handleModalClose,
  onSuccess = () => {},
}: {
  isModalOpen: boolean;
  cooperativeDetails: SingleCooperativeDetails;
  type: InstrumentType[];
  mode?: "linkToCoop" | "selection";
  handleModalClose: () => void;
  onSuccess?: (selected: SingleMemberBankAccount | SingleDebitCard) => void;
}) => {
  const [instrumentType, setInstrumentType] = useState<InstrumentType>(type[0]);
  const [selectedInnstrument, setSelectedInstrument] = useState<
    SingleMemberBankAccount | SingleDebitCard
  >();
  const [showIframe, setShowIframe] = useState(false);
  const [authUrl, setAuthUrl] = useState<string>();

  const iframeRef = useRef<HTMLIFrameElement>(null);

  const { environment } = useEnvironment();

  const {
    modal,
    modalOpen,
    handleModalClose: closeModal,
    handleModalOpen,
  } = useModalControl();

  const {
    data: memberBankAccounts,
    isLoading: loadingMemberBankAccounts,
    refetch: refetchmemberBankAccount,
  } = useGetMemberBankAccountsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const {
    data: userDebitCards,
    isLoading: loadingUserDebitCards,
    refetch: refetchUserDebitCards,
  } = useGetUserDebitCardsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const { initiatingCardTokenization, doCardTokenization } =
    useCardTokenization();

  const [setUpDirectDebit, { isLoading: settingDirectDebit }] =
    useSetUpDirectDebitMutation();

  useEffect(() => {
    if (!showIframe) return;

    const interval = setInterval(() => {
      try {
        const iframe = iframeRef.current;

        const currentUrl = iframe?.contentWindow?.location.href;

        if (!currentUrl) return;

        if (
          currentUrl.toLowerCase().includes("thriftcorp") &&
          currentUrl !== "about:blank"
        ) {
          setShowIframe(false);
          setAuthUrl(undefined);
          onSuccess(selectedInnstrument!);
        }
      } catch {}
    }, 500);

    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [showIframe]);

  useEffect(() => {
    setInstrumentType(type[0]);
  }, [type]);

  return (
    <>
      <ModalComponent
        isModalOpen={isModalOpen && !showIframe}
        onModalClose={() => {
          handleModalClose();
          setInstrumentType(type[0]);
        }}
        title={`Link ${instrumentType === "bank" ? "Bank Account" : "Card"}`}
        showHeader
        showMobileTopBar
        modalClassName="!min-h-[480px]"
        childrenClassName="flex flex-col"
        hasButtons={false}
        closeOnOutsideClick
      >
        <div className="flex flex-col flex-1 gap-3">
          {instrumentType === "bank" ? (
            <>
              {loadingMemberBankAccounts
                ? [1, 2].map((k) => {
                    return <BankLoader key={`${k}`} />;
                  })
                : memberBankAccounts?.data?.map((account) => {
                    return (
                      <Checkbox
                        key={`bank-account-${account.id}`}
                        checked={selectedInnstrument?.id === account.id}
                        className={`
                            w-full px-4 py-3 bg-light-green-bg rounded-lg !gap-4
                            bg-[url('assets/images/card_bg.svg')] bg-no-repeat
                            bg-cover md:bg-contain bg-center
                            `}
                        onCheck={() => {
                          setSelectedInstrument(account);
                        }}
                        onUnCheck={() => {}}
                        text={
                          <p className="flex flex-1 items-center gap-4 justify-between">
                            <span className="text-base font-medium text-pry-green-200">
                              {account.AccountNumber}
                            </span>

                            <span className="text-sm w-full flex flex-col text-right">
                              <span className="line-clamp-1 break-all font-medium">
                                {account.AccountName}
                              </span>
                              <span className="line-clamp-1 break-all text-pry-dark-green-400/70">
                                {account.BankName}
                              </span>
                            </span>
                          </p>
                        }
                      />
                    );
                  })}
            </>
          ) : (
            <>
              {loadingUserDebitCards
                ? [1, 2].map((k) => {
                    return <DebitCardLoader key={`${k}`} />;
                  })
                : userDebitCards?.data?.map((card) => {
                    return (
                      <Checkbox
                        key={`debit-card-${card.id}`}
                        checked={selectedInnstrument?.id === card.id}
                        className={`
                            w-full px-4 py-3 bg-light-green-bg rounded-lg !gap-4
                            bg-[url('assets/images/card_bg.svg')] bg-no-repeat
                            bg-cover md:bg-contain bg-center
                            `}
                        onCheck={() => {
                          setSelectedInstrument(card);
                        }}
                        onUnCheck={() => {}}
                        text={
                          <p className="flex flex-1 items-center gap-4">
                            <span className="text-base font-bold text-pry-dark-green-400/80">
                              ****{card?.CardDetail?.last4?.slice(-4)}
                            </span>

                            <span className="text-sm text-pry-dark-green-400/70">
                              {card?.CardDetail?.exp_month}/
                              {card?.CardDetail?.exp_year}
                            </span>

                            <Icon
                              svg={
                                card?.CardDetail?.card_type === "visa"
                                  ? "visa"
                                  : "mastercard"
                              }
                              width={
                                card?.CardDetail?.card_type === "visa" ? 32 : 24
                              }
                              height={
                                card?.CardDetail?.card_type === "visa" ? 32 : 24
                              }
                            />
                          </p>
                        }
                      />
                    );
                  })}
            </>
          )}

          <Button
            type="button"
            text={`Add another ${
              instrumentType === "bank" ? "bank account" : "card"
            }`}
            onClick={async () => {
              if (instrumentType === "card") {
                await doCardTokenization({
                  onSuccess: () => {
                    refetchUserDebitCards();
                  },
                });
              } else {
                handleModalOpen("addBankAccountModal");
              }
            }}
            icon="plus"
            variation="plain"
            loading={initiatingCardTokenization}
            disabled={initiatingCardTokenization}
          />
        </div>

        <div className="flex flex-col gap-3 mt-10">
          <Button
            type="button"
            text={`Link ${instrumentType === "bank" ? "Bank Account" : "Card"}`}
            variation="primary"
            disabled={!selectedInnstrument || settingDirectDebit}
            loading={settingDirectDebit}
            onClick={async () => {
              if (
                (instrumentType === "bank" &&
                  !(selectedInnstrument as SingleMemberBankAccount)
                    ?.isDirectDebit) ||
                mode === "linkToCoop"
              ) {
                const resp = await setUpDirectDebit({
                  coop:
                    mode === "linkToCoop" ? cooperativeDetails.id : undefined,
                  mode: instrumentType,
                  id: selectedInnstrument?.id!,
                  callback_url: window.location.href,
                });

                if (resp.data?.success) {
                  if (
                    instrumentType === "bank" &&
                    !(selectedInnstrument as SingleMemberBankAccount)
                      ?.isDirectDebit
                  ) {
                    setAuthUrl(resp.data.data.redirect_url);
                    setShowIframe(true);
                  } else {
                    onSuccess(selectedInnstrument!);
                  }
                }
              } else {
                onSuccess(selectedInnstrument!);
              }
            }}
          />

          {type.length > 1 ? (
            <Button
              type="button"
              text={`Link ${
                instrumentType === "bank" ? "Card" : "Bank Account"
              } Instead`}
              variation="plain"
              onClick={() => {
                setInstrumentType(instrumentType === "bank" ? "card" : "bank");
                setSelectedInstrument(undefined);
              }}
            />
          ) : undefined}
        </div>
      </ModalComponent>

      <AddBankAccountModal
        modalOpen={modalOpen && modal === "addBankAccountModal"}
        onModalClose={() => {
          closeModal();
        }}
        onSuccess={() => {
          refetchmemberBankAccount();
          closeModal();
        }}
        type="member"
      />

      {ReactDOM.createPortal(
        <div
          className={classNames("fixed bg-black/70 inset-0 z-[999] hidden", {
            "!flex flex-col": showIframe && authUrl,
            "max-sm:pt-[44px]": environment === "mobile",
          })}
        >
          {environment === "mobile" ? (
            <div className="bg-transparent sm:hidden flex p-2 items-center justify-end">
              <button
                type="button"
                className="w-fit"
                onClick={() => {
                  setShowIframe(false);
                  setAuthUrl(undefined);
                }}
              >
                <Icon
                  svg={"close-icon-2"}
                  width={28}
                  height={28}
                  className="text-white"
                />
              </button>
            </div>
          ) : undefined}

          <iframe
            ref={iframeRef}
            src={authUrl}
            title="Paystack Authorization"
            className="w-full h-full max-sm:rounded-t-lg"
            allow="payment"
            allowFullScreen
          />
        </div>,
        document.getElementById("portal")!
      )}
    </>
  );
};
